import React, { useCallback, useMemo } from "react";
import {
  Alert,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import WhatsAppContactsPicker from "../WhatsAppContactsPicker";
import {
  buildAudienceNarrative,
  buildAudienceStatusBadges,
  isManualAudience,
  normalizePositiveInteger,
} from "./audienceUtils";

export default function AudienceReview({
  audienceChoice,
  filters,
  audiencePlan,
  audienceSummary,
  previewRows,
  selectedIds,
  setSelectedIds,
  onFiltersChange,
}) {
  const summaryBadges = buildAudienceStatusBadges(audienceSummary);
  const narrative = buildAudienceNarrative({
    audienceKey: audienceChoice,
    filters,
    summary: audienceSummary,
  });
  const manualSelection = isManualAudience(audienceChoice);
  const preferredLimit = normalizePositiveInteger(audienceSummary?.preferredLimit || filters?.preferred_limit);
  const selectedCount = selectedIds instanceof Set ? selectedIds.size : Array.isArray(selectedIds) ? selectedIds.length : 0;
  const pickerFilterModel = useMemo(
    () => ({
      ...filters,
      lead_status: filters?.status || "",
      status: filters?.status || "",
    }),
    [filters]
  );
  const handlePickerFilterChange = useCallback(
    (nextFilters) => {
      onFiltersChange({
        ...filters,
        q: nextFilters?.q || "",
        source: nextFilters?.source || "",
        status: nextFilters?.lead_status || nextFilters?.status || "",
        contact_group_id: nextFilters?.contact_group_id || "",
        contacted: nextFilters?.contacted || "",
        contacted_last_days: nextFilters?.contacted_last_days || "",
        exclude_campaign_id: nextFilters?.exclude_campaign_id || "",
        campaign_id: nextFilters?.campaign_id || "",
      });
    },
    [filters, onFiltersChange]
  );
  const groupedReasons = useMemo(() => {
    if (audienceSummary?.suppressedBreakdown && typeof audienceSummary.suppressedBreakdown === "object") {
      return Object.entries(audienceSummary.suppressedBreakdown);
    }

    const counts = new Map();
    (audienceSummary?.suppressedContacts || []).forEach((item) => {
      const label = item?.reason_label || "Suppressed";
      counts.set(label, (counts.get(label) || 0) + 1);
    });

    return Array.from(counts.entries());
  }, [audienceSummary?.suppressedBreakdown, audienceSummary?.suppressedContacts]);

  const budgetExceededCount = useMemo(() => {
    const breakdown = audienceSummary?.suppressedBreakdown || {};
    return Object.entries(breakdown)
      .filter(([key]) => key.toLowerCase().includes("budget"))
      .reduce((sum, [, count]) => sum + Number(count), 0);
  }, [audienceSummary?.suppressedBreakdown]);

  return (
    <Stack spacing={2.5}>
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #E5E7EB" }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" fontWeight={900}>
            Review this audience
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {narrative}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label={`Matched ${summaryBadges.matched.toLocaleString()}`} />
            <Chip color="primary" label={`Will send ${summaryBadges.sendable.toLocaleString()}`} />
            <Chip color="warning" label={`Will skip ${summaryBadges.skipped.toLocaleString()}`} />
            {manualSelection ? (
              <Chip color="success" variant="outlined" label={`Selected ${selectedCount.toLocaleString()}`} />
            ) : null}
            {preferredLimit ? (
              <Chip variant="outlined" label={`Preferred limit ${preferredLimit.toLocaleString()}`} />
            ) : null}
          </Stack>

          {groupedReasons.length > 0 ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {groupedReasons.map(([label, count]) => (
                <Chip key={label} size="small" variant="outlined" label={`${label}: ${count}`} />
              ))}
            </Stack>
          ) : null}

          {budgetExceededCount > 0 ? (
            <Alert severity="warning" sx={{ mt: 0.5 }}>
              <strong>{budgetExceededCount.toLocaleString()} contact{budgetExceededCount !== 1 ? "s" : ""}</strong> were excluded because the WhatsApp broadcast budget has been reached. Contact IT to increase the budget for WhatsApp broadcast.
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      {!manualSelection ? (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #E5E7EB" }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" fontWeight={900}>
              Recipient sample
            </Typography>
            <Typography variant="body2" color="text.secondary">
              The sender only needs a quick confidence check here. The final send will still use the full audience definition and re-apply guardrails.
            </Typography>

            <Typography variant="caption" color="text.secondary">
              Showing {(previewRows || []).length.toLocaleString()} sampled recipients.
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ maxHeight: 280, overflowY: "auto", pr: 0.5 }}>
              {(previewRows || []).length > 0 ? (previewRows || []).map((row) => (
                <Chip
                  key={row.id}
                  size="small"
                  variant="outlined"
                  label={row.name ? `${row.name} <${row.phone}>` : row.phone}
                />
              )) : (
                <Typography variant="body2" color="text.secondary">
                  No sample recipients available yet. Go back and run Find Audience.
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: "1px solid #E5E7EB" }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" fontWeight={900}>
              Pick recipients for this custom audience
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Use the contact picker below to select exact recipients. You can still import contacts or build a contact database here before moving on.
            </Typography>
            <Divider />
            <WhatsAppContactsPicker
              embedded
              selectionEnabled
              selectionModel={selectedIds}
              onSelectionChange={setSelectedIds}
              filterModel={pickerFilterModel}
              onFilterChange={handlePickerFilterChange}
              limit={Math.max(50, Math.min(Number(filters.limit || 200), 500))}
            />
            <Typography variant="caption" color="text.secondary">
              Guardrails and send limits will still be re-applied when the broadcast is launched.
            </Typography>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
